import { useState, useEffect, useRef } from 'react';

interface UseAnimatedValueOptions {
  value: number;
  duration?: number;
}

export const useAnimatedValue = ({ 
  value, 
  duration = 600
}: UseAnimatedValueOptions): number => {
  const [displayValue, setDisplayValue] = useState(value);
  const previousValue = useRef(value);
  const animationRef = useRef<number>();

  useEffect(() => {
    const startValue = previousValue.current;
    const endValue = value;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing: easeOutExpo for snappy feel
      const easeOutExpo = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      const currentValue = startValue + (endValue - startValue) * easeOutExpo;
      
      setDisplayValue(currentValue);
      
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        previousValue.current = endValue;
      }
    };

    if (Math.abs(startValue - endValue) > 0.01) {
      animationRef.current = requestAnimationFrame(animate);
    } else {
      setDisplayValue(endValue);
      previousValue.current = endValue;
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [value, duration]);

  return displayValue;
};

export default useAnimatedValue;
